/**
 * BookingDetailPage - View booking details with classes, faculty, and payments
 */

import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  getBookingDetails,
  cancelBooking,
  payBookingBalance,
  createBookingCheckoutSession,
  createDepositCheckoutSession,
  verifyBookingCheckout,
  createCardSetupSession,
  confirmCardUpdate,
  describeSavedCard,
  submitMarketingMaterials,
  type BookingDetails,
  formatBookingStatus,
  getStatusColor,
} from '../../api/studios';
import { formatPrice } from '../../api/events';
import {
  submitBookingQuestion,
  formatInquiryStatus,
  getStatusColor as getInquiryStatusColor,
} from '../../api/inquiries';
import { DashboardMobileNav } from '../../components/dashboard';
import { BalanceSchedule, formatScheduleAmount } from '../../components/booking/BalanceSchedule';
import { formatDateDisplay } from '../../utils/date';

export default function BookingDetailPage() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [booking, setBooking] = useState<BookingDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [isPayingBalance, setIsPayingBalance] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSubmittingMaterials, setIsSubmittingMaterials] = useState(false);
  const [materialsSuccess, setMaterialsSuccess] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [signupMethod, setSignupMethod] = useState('');
  const [classCost, setClassCost] = useState('');
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [questionText, setQuestionText] = useState('');
  const [isSubmittingQuestion, setIsSubmittingQuestion] = useState(false);
  const [questionSuccess, setQuestionSuccess] = useState(false);
  // Card on file update (SDA-292)
  const [isUpdatingCard, setIsUpdatingCard] = useState(false);
  const [cardNotice, setCardNotice] = useState<{ kind: 'success' | 'info' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (bookingId) {
      loadBooking(bookingId);
    }
  }, [bookingId]);

  // Scroll to hash target (e.g., #marketing-materials) after booking loads
  useEffect(() => {
    if (!isLoading && booking && window.location.hash) {
      // Use setTimeout to ensure React has flushed DOM updates and browser has painted
      const timer = setTimeout(() => {
        const el = document.getElementById(window.location.hash.slice(1));
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isLoading, booking]);

  // Handle Stripe Checkout return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout_success') === 'true') {
      const sessionId = params.get('session_id');
      setPaymentSuccess(true);
      // Clean up URL params
      window.history.replaceState({}, '', window.location.pathname);

      if (bookingId && sessionId) {
        const verify = async () => {
          setIsVerifying(true);
          try {
            let retries = 0;
            const maxRetries = 3;
            while (retries < maxRetries) {
              const result = await verifyBookingCheckout(bookingId, sessionId);
              if (result.status === 'paid') {
                await loadBooking(bookingId);
                return;
              }
              if (result.status === 'failed') {
                setPaymentError('Payment verification failed. Please contact support if you were charged.');
                return;
              }
              // status === 'pending' — wait and retry
              retries++;
              if (retries < maxRetries) {
                await new Promise((r) => setTimeout(r, 2000));
              }
            }
            // Still pending after all retries — reload anyway (best effort)
            await loadBooking(bookingId);
          } catch {
            setPaymentError('Could not verify payment status. Please refresh the page.');
          } finally {
            setIsVerifying(false);
          }
        };
        verify();
      } else if (bookingId) {
        // No session_id in URL — fall back to delayed reload
        const timer = setTimeout(() => loadBooking(bookingId), 2000);
        return () => clearTimeout(timer);
      }
    } else if (params.get('checkout_cancelled') === 'true') {
      setPaymentError('Payment was cancelled. You can try again when ready.');
      // Clean up URL params
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('card_updated') === 'true') {
      // Back from the card-update Checkout (SDA-292) — nothing was charged.
      const sessionId = params.get('session_id');
      window.history.replaceState({}, '', window.location.pathname);
      if (sessionId) {
        confirmCardUpdate(sessionId)
          .then((card) =>
            setCardNotice({ kind: 'success', text: `Card updated. Future payments will use your ${describeSavedCard(card)}.` }),
          )
          .catch(() =>
            setCardNotice({ kind: 'error', text: 'We could not confirm your new card. Please refresh the page or try again.' }),
          )
          .finally(() => {
            if (bookingId) loadBooking(bookingId);
          });
      }
    } else if (params.get('card_cancelled') === 'true') {
      setCardNotice({ kind: 'info', text: 'Card update cancelled. Your card on file was not changed.' });
      window.history.replaceState({}, '', window.location.pathname);
    }
    return undefined;
  }, []);

  const loadBooking = async (id: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getBookingDetails(id);
      setBooking(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load booking');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayBalance = async () => {
    if (!bookingId) return;

    try {
      setIsPayingBalance(true);
      setPaymentError(null);
      await payBookingBalance(bookingId);
      setPaymentSuccess(true);
      setPaymentError(null);
      // Reload booking to reflect updated payment status
      await loadBooking(bookingId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Payment failed';
      // If no payment method on file, redirect to Stripe Checkout
      if (message.includes('redirected to complete payment')) {
        try {
          const { checkoutUrl } = await createBookingCheckoutSession(bookingId);
          window.location.href = checkoutUrl;
          return;
        } catch (checkoutErr) {
          setPaymentError(checkoutErr instanceof Error ? checkoutErr.message : 'Failed to start checkout');
          return;
        }
      }
      setPaymentError(message);
    } finally {
      setIsPayingBalance(false);
    }
  };

  const handleUpdateCard = async () => {
    if (!bookingId) return;
    try {
      setIsUpdatingCard(true);
      setCardNotice(null);
      const { checkoutUrl } = await createCardSetupSession(`/dashboard/bookings/${bookingId}`);
      window.location.href = checkoutUrl;
    } catch (err) {
      setCardNotice({ kind: 'error', text: err instanceof Error ? err.message : 'Failed to start the card update' });
      setIsUpdatingCard(false);
    }
  };

  const handlePayDeposit = async () => {
    if (!bookingId) return;

    try {
      setIsPayingBalance(true);
      setPaymentError(null);
      const { checkoutUrl } = await createDepositCheckoutSession(bookingId);
      window.location.href = checkoutUrl;
    } catch (err) {
      setPaymentError(err instanceof Error ? err.message : 'Failed to start deposit checkout');
      setIsPayingBalance(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!bookingId) return;

    try {
      setIsCancelling(true);
      setCancelError(null);
      await cancelBooking(bookingId);
      setShowCancelModal(false);
      // Navigate back to bookings list after successful cancellation
      navigate('/dashboard/bookings', {
        state: { message: 'Booking cancelled successfully' },
      });
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : 'Failed to cancel booking');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleSubmitQuestion = async () => {
    if (!questionText.trim() || !bookingId) return;
    setIsSubmittingQuestion(true);
    try {
      await submitBookingQuestion({ bookingId, message: questionText.trim() });
      setQuestionText('');
      setShowQuestionModal(false);
      setQuestionSuccess(true);
      setTimeout(() => setQuestionSuccess(false), 3000);
      loadBooking(bookingId);
    } catch {
      // Error handled inline in modal
    } finally {
      setIsSubmittingQuestion(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-warm-50">
        <Header user={user} logout={logout} />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-warm-100 rounded w-1/4" />
            <div className="card space-y-4">
              <div className="h-6 bg-warm-100 rounded w-1/3" />
              <div className="h-4 bg-warm-100 rounded w-1/2" />
              <div className="h-4 bg-warm-100 rounded w-1/4" />
            </div>
            <div className="card space-y-4">
              <div className="h-6 bg-warm-100 rounded w-1/4" />
              <div className="h-20 bg-warm-100 rounded" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen bg-warm-50">
        <Header user={user} logout={logout} />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="card text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-danger-100 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-danger-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-warm-900 mb-2">
              {error || 'Booking not found'}
            </h3>
            <p className="text-warm-600 mb-6">
              We couldn't find the booking you're looking for.
            </p>
            <Link to="/dashboard/bookings" className="btn-primary">
              Back to Bookings
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const statusColors = getStatusColor(booking.status);
  const isPending = booking.status === 'pending';
  const isConfirmed = booking.status === 'confirmed';
  const canCancel = isPending || isConfirmed;
  const isCancelled = booking.status === 'cancelled';
  // Per-class auto-charge schedule (multi-class bookings charged on different dates).
  const schedule = !isCancelled ? booking.balance_schedule ?? null : null;

  // Don't show payment alert for cancelled bookings
  const needsPayment =
    !isCancelled &&
    booking.balance_amount > 0 &&
    (booking.balance_status === 'pending' || booking.balance_status === 'deposit_paid');

  // Calculate cancellation refund tier based on earliest class date
  const earliestClassDate = booking.classes
    .map((c) => c.class_date)
    .filter(Boolean)
    .sort()[0];
  const daysUntilClass = earliestClassDate
    ? Math.ceil(
        (new Date(earliestClassDate + 'T12:00:00').getTime() - Date.now()) / (1000 * 60 * 60 * 24)
      )
    : null;

  return (
    <div className="min-h-screen bg-warm-50">
      <Header user={user} logout={logout} />
      <DashboardMobileNav />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back link */}
        <Link
          to="/dashboard/bookings"
          className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium mb-4"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to My Bookings
        </Link>

        {/* Breadcrumb (desktop) */}
        <nav className="hidden sm:block mb-6">
          <ol className="flex items-center gap-2 text-sm">
            <li>
              <Link to="/dashboard" className="text-primary-600 hover:text-primary-700">
                Dashboard
              </Link>
            </li>
            <li className="text-warm-400">/</li>
            <li>
              <Link
                to="/dashboard/bookings"
                className="text-primary-600 hover:text-primary-700"
              >
                My Bookings
              </Link>
            </li>
            <li className="text-warm-400">/</li>
            <li className="text-warm-600 truncate max-w-[150px]">{booking.show_name}</li>
          </ol>
        </nav>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
              {booking.show_logo_url ? (
                <img
                  src={booking.show_logo_url}
                  alt={booking.show_name}
                  className="w-full h-full object-cover rounded-lg"
                />
              ) : (
                <span className="text-primary-500 text-3xl font-display font-bold">
                  {booking.show_name.charAt(0)}
                </span>
              )}
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-display font-bold text-warm-900 mb-1">
                {booking.show_name}
              </h1>
              <p className="text-warm-600">
                Booking {booking.booking_number || `#${booking.id.slice(0, 8).toUpperCase()}`}
              </p>
              <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-medium ${statusColors}`}>
                {formatBookingStatus(booking.status)}
              </span>
            </div>
          </div>

          {canCancel && (
            <div className="flex gap-2">
              {daysUntilClass !== null && daysUntilClass < 15 ? (
                <div className="p-3 bg-warning-50 border border-warning-200 rounded-lg max-w-xs">
                  <p className="text-sm text-warning-800">
                    Cancellations must be made 15+ days before class. Please contact support to discuss options.
                  </p>
                </div>
              ) : (
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="btn border border-danger-300 text-danger-600 hover:bg-danger-50"
                >
                  Cancel Booking
                </button>
              )}
            </div>
          )}
        </div>

        {/* Payment Verifying */}
        {isVerifying && (
          <div className="mb-6 p-4 bg-primary-50 border border-primary-200 rounded-lg flex items-center gap-3">
            <svg className="w-5 h-5 text-primary-600 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="font-medium text-primary-800">Verifying payment...</p>
          </div>
        )}

        {/* Payment Success */}
        {paymentSuccess && !isVerifying && (
          <div className="mb-6 p-4 bg-success-50 border border-success-200 rounded-lg flex items-start gap-3">
            <svg className="w-5 h-5 text-success-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            <p className="font-medium text-success-800">
              Balance paid successfully! Your booking is now fully paid.
            </p>
          </div>
        )}

        {/* Action Alert */}
        {needsPayment && !paymentSuccess && (
          <div className="mb-6 p-4 bg-warning-50 border border-warning-200 rounded-lg flex items-start gap-3">
            <svg
              className="w-5 h-5 text-warning-600 flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <p className="font-medium text-warning-800">
                {booking.balance_status === 'pending'
                  ? 'Deposit payment required'
                  : schedule
                  ? 'Balance charged class by class'
                  : 'Balance payment due'}
              </p>
              <p className="text-sm text-warning-700 mt-1">
                {booking.balance_status === 'pending'
                  ? 'Your deposit is required to confirm this booking.'
                  : schedule
                  ? schedule.next_charge?.retrying
                    ? `Your last charge didn't go through: ${formatScheduleAmount(schedule.next_charge.amount_cents)} is due now. We'll retry your saved card every day until the class, or you can pay now.`
                    : schedule.next_charge
                    ? `Next charge: ${formatScheduleAmount(schedule.next_charge.amount_cents)} on ${formatDateDisplay(schedule.next_charge.date)}. ${formatScheduleAmount(schedule.remaining_cents)} left in total. You can also pay it all now.`
                    : `${formatScheduleAmount(schedule.remaining_cents)} left to pay.`
                  : `Balance of ${formatPrice(booking.balance_amount)} is due before your class.`}
              </p>
              {booking.balance_status !== 'pending' && (
                <button
                  type="button"
                  onClick={handleUpdateCard}
                  disabled={isUpdatingCard}
                  className="mt-2 text-sm font-medium text-warning-800 underline hover:text-warning-900 disabled:opacity-50"
                >
                  {isUpdatingCard ? 'Redirecting...' : 'Update card'}
                </button>
              )}
            </div>
            {booking.balance_status === 'pending' ? (
              <button
                onClick={handlePayDeposit}
                disabled={isPayingBalance}
                className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50"
              >
                {isPayingBalance ? 'Processing...' : 'Pay deposit'}
              </button>
            ) : (
              <button
                onClick={() => setShowPayModal(true)}
                className="btn-primary text-sm flex items-center gap-2"
              >
                Pay Now
              </button>
            )}
          </div>
        )}

        {/* Card update result (SDA-292) */}
        {cardNotice && (
          <div
            className={`mb-6 p-4 rounded-lg border text-sm ${
              cardNotice.kind === 'success'
                ? 'bg-success-50 border-success-200 text-success-700'
                : cardNotice.kind === 'error'
                ? 'bg-danger-50 border-danger-200 text-danger-700'
                : 'bg-warm-50 border-warm-200 text-warm-700'
            }`}
            role="status"
          >
            {cardNotice.text}
          </div>
        )}

        {/* Payment Error */}
        {paymentError && (
          <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-start gap-3">
            <svg className="w-5 h-5 text-danger-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-medium text-danger-800">Payment failed</p>
              <p className="text-sm text-danger-700 mt-1">{paymentError}</p>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* Classes */}
          <div className="card">
            <h2 className="text-lg font-semibold text-warm-900 mb-4">
              Classes ({booking.classes.length})
            </h2>
            {(booking.venue_name || booking.venue_address) && (
              <p className="-mt-2 mb-4 flex items-start gap-1.5 text-sm text-warm-600">
                <svg
                  className="w-4 h-4 mt-0.5 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span>
                  Venue: {[booking.venue_name, booking.venue_address].filter(Boolean).join(', ')}
                </span>
              </p>
            )}
            <div className="space-y-4">
              {booking.classes.map((cls, index) => (
                <div
                  key={cls.id}
                  className={`p-4 rounded-lg bg-warm-50 ${
                    index < booking.classes.length - 1 ? 'border-b border-warm-100' : ''
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <h3 className="font-medium text-warm-900">{cls.class_option_name}</h3>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-warm-600">
                        <span className="flex items-center gap-1">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          {cls.class_date
                            ? new Date(cls.class_date + 'T12:00:00').toLocaleDateString('en-US', {
                                weekday: 'long',
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric',
                              })
                            : 'Date TBD'}
                        </span>
                        <span className="flex items-center gap-1">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          {cls.time_slot || 'Time TBD'}
                        </span>
                        <span className="flex items-center gap-1">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                            />
                          </svg>
                          {cls.participant_count} participants
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          cls.status === 'confirmed'
                            ? 'bg-success-100 text-success-700'
                            : cls.status === 'completed'
                            ? 'bg-primary-100 text-primary-700'
                            : 'bg-warm-100 text-warm-600'
                        }`}
                      >
                        {cls.status.charAt(0).toUpperCase() + cls.status.slice(1)}
                      </span>
                    </div>
                  </div>

                  {/* Instructor Card */}
                  {cls.faculty_name && (
                    <div className="mt-4 pt-4 border-t border-warm-100">
                      <p className="text-xs font-semibold text-warm-500 uppercase tracking-wider mb-3">
                        Your Instructor
                      </p>
                      <div
                        className="rounded-xl overflow-hidden"
                        style={{ background: 'linear-gradient(135deg, #eef4ff 0%, #dce8ff 100%)' }}
                      >
                        <div className="p-5 flex items-start gap-4">
                          <div className="flex-shrink-0">
                            <div className="w-16 h-16 rounded-full overflow-hidden ring-2 ring-white shadow-md bg-primary-100 flex items-center justify-center">
                              {cls.faculty_headshot_url ? (
                                <img
                                  src={cls.faculty_headshot_url}
                                  alt={cls.faculty_name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <span className="text-primary-600 font-bold text-xl">
                                  {cls.faculty_name.charAt(0)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-warm-900 text-base leading-tight">
                              {cls.faculty_name}
                            </p>
                            {cls.faculty_bio ? (
                              <p className="text-sm text-warm-600 mt-1.5 leading-relaxed line-clamp-3">
                                {cls.faculty_bio}
                              </p>
                            ) : (
                              <p className="text-sm text-warm-400 mt-1">Broadway performer</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>


          {/* Payment Summary */}
          <div className="card">
            <h2 className="text-lg font-semibold text-warm-900 mb-4">Payment Summary</h2>
            <div className="space-y-3">
              <div className="flex justify-between text-warm-600">
                <span>Subtotal</span>
                <span>{formatPrice(booking.subtotal)}</span>
              </div>
              {booking.travel_surcharge > 0 && (
                <div className="flex justify-between text-warm-600">
                  <span>Travel Surcharge</span>
                  <span>{formatPrice(booking.travel_surcharge)}</span>
                </div>
              )}
              {booking.marketing_fee > 0 && (
                <div className="flex justify-between text-warm-600">
                  <span>Marketing Materials</span>
                  <span>{formatPrice(booking.marketing_fee)}</span>
                </div>
              )}
              {booking.discount_cents > 0 && (
                <div className="flex justify-between text-success-600">
                  <span>Discount{booking.coupon_code ? ` (${booking.coupon_code})` : ''}</span>
                  <span>-{formatPrice(booking.discount_cents)}</span>
                </div>
              )}
              <div className="pt-3 border-t border-warm-200 flex justify-between font-semibold text-warm-900">
                <span>Total</span>
                <span>{formatPrice(booking.total_amount)}</span>
              </div>
            </div>

            {/* Payment Status */}
            <div className="mt-6 pt-4 border-t border-warm-100">
              <h3 className="text-sm font-medium text-warm-700 mb-3">Payment Status</h3>
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-3 rounded-full ${
                    isCancelled
                      ? 'bg-warm-400'
                      : booking.balance_status === 'paid' || (booking.balance_status === 'deposit_paid' && booking.balance_amount <= 0)
                      ? 'bg-success-500'
                      : booking.balance_status === 'deposit_paid'
                      ? 'bg-warning-500'
                      : 'bg-danger-500'
                  }`}
                />
                <span className="text-warm-600">
                  {isCancelled
                    ? 'Booking cancelled - no payment required'
                    : booking.balance_status === 'paid' || (booking.balance_status === 'deposit_paid' && booking.balance_amount <= 0)
                    ? 'Fully paid'
                    : booking.balance_status === 'deposit_paid' && schedule
                    ? `Deposit paid • ${formatScheduleAmount(schedule.remaining_cents)} left, charged class by class`
                    : booking.balance_status === 'deposit_paid'
                    ? `Deposit paid • Balance of ${formatPrice(booking.balance_amount)} due`
                    : 'Payment pending'}
                </span>
              </div>
            </div>

            {/* Balance Schedule — multi-class bookings charged on different dates */}
            {schedule && (
              <div className="mt-4 pt-4 border-t border-warm-100">
                <h3 className="text-sm font-medium text-warm-700 mb-3">Balance Schedule</h3>
                <BalanceSchedule schedule={schedule} audience="studio" />
              </div>
            )}

            {/* Payment History — inside Payment Summary card */}
            {booking.payments && booking.payments.length > 0 && (
              <div className="mt-4 pt-4 border-t border-warm-100">
                <h3 className="text-sm font-medium text-warm-700 mb-3">Payment History</h3>
                <div className="space-y-2">
                  {booking.payments.map((payment) => (
                    <div key={payment.id} className="flex items-center justify-between text-sm">
                      <div>
                        <span className="text-warm-900 font-medium">
                          {payment.type === 'deposit' ? 'Deposit Paid' : 'Balance Paid'}
                        </span>
                        <span className="text-warm-400 ml-2">
                          {new Date(payment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                      <span className="text-success-600 font-medium">{formatScheduleAmount(payment.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Questions */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-warm-900">
                Questions {booking.inquiries && booking.inquiries.length > 0 && (
                  <span className="text-sm font-normal text-warm-500">({booking.inquiries.length})</span>
                )}
              </h2>
              <button
                onClick={() => setShowQuestionModal(true)}
                className="btn-primary text-sm"
              >
                Ask a Question
              </button>
            </div>

            {questionSuccess && (
              <div className="mb-4 p-3 bg-success-50 border border-success-200 rounded-lg text-success-700 text-sm">
                Your question has been submitted! We'll respond within 24 hours.
              </div>
            )}

            {!booking.inquiries || booking.inquiries.length === 0 ? (
              <p className="text-warm-500 text-sm">No questions yet. Have a question about this booking? Click "Ask a Question" above.</p>
            ) : (
              <div className="space-y-3">
                {booking.inquiries.map((inq) => (
                  <Link
                    key={inq.id}
                    to={`/inquiry/status/${inq.status_token}`}
                    className="block p-3 rounded-lg border border-warm-200 hover:border-primary-300 hover:shadow-sm transition-all"
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-sm text-warm-900 line-clamp-2">{inq.message}</p>
                      <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${getInquiryStatusColor(inq.status)}`}>
                        {formatInquiryStatus(inq.status)}
                      </span>
                    </div>
                    <p className="text-xs text-warm-500">
                      {new Date(inq.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                    {inq.response_message && (() => {
                      const text = inq.response_message.replace(/<[^>]*>/g, '').trim();
                      return (
                        <div className="mt-2 p-2 bg-warm-50 rounded text-sm text-warm-700">
                          <span className="font-medium">Response:</span> {text.length > 150 ? text.slice(0, 150) + '...' : text}
                        </div>
                      );
                    })()}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Marketing Materials */}
          {booking.marketing_materials && (
            <div id="marketing-materials" className="card">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">Marketing Materials</h2>
              {booking.marketing_materials_completed_at ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-success-100 flex items-center justify-center">
                      <svg className="w-5 h-5 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-warm-900">Completed</p>
                      <p className="text-sm text-warm-500">
                        {new Date(booking.marketing_materials_completed_at).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>
                  {/* Show submitted values */}
                  <div className="border-t border-warm-100 pt-4 space-y-3">
                    {booking.marketing_logo_url && (
                      <div className="flex items-start gap-3">
                        <span className="text-sm text-warm-500 w-28 shrink-0">Logo</span>
                        <img src={booking.marketing_logo_url} alt="Studio logo" className="h-12 w-auto rounded border border-warm-200" />
                      </div>
                    )}
                    {booking.marketing_signup_method && (
                      <div className="flex items-start gap-3">
                        <span className="text-sm text-warm-500 w-28 shrink-0">Sign-up Method</span>
                        <span className="text-sm text-warm-900">{booking.marketing_signup_method}</span>
                      </div>
                    )}
                    {booking.marketing_class_cost && (
                      <div className="flex items-start gap-3">
                        <span className="text-sm text-warm-500 w-28 shrink-0">Class Cost</span>
                        <span className="text-sm text-warm-900">{booking.marketing_class_cost}</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : materialsSuccess ? (
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-success-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="font-medium text-warm-900">Marketing materials submitted successfully!</p>
                </div>
              ) : (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!bookingId) return;
                    setIsSubmittingMaterials(true);
                    setError(null);
                    try {
                      await submitMarketingMaterials(bookingId, {
                        logo: logoFile || undefined,
                        signupMethod,
                        classCost,
                      });
                      setMaterialsSuccess(true);
                      await loadBooking(bookingId);
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Failed to submit marketing materials');
                    } finally {
                      setIsSubmittingMaterials(false);
                    }
                  }}
                  className="space-y-6"
                >
                  <p className="text-warm-600">
                    You selected the Marketing Materials add-on for this booking. Please fill out the information below so we can prepare your custom promotional materials.
                  </p>

                  {/* Logo Upload */}
                  <div>
                    <label className="block text-sm font-medium text-warm-900 mb-1">
                      Studio Logo <span className="text-warm-400 font-normal">(optional)</span>
                    </label>
                    <p className="text-sm text-warm-500 mb-3">
                      Please upload your studio logo so we can include it on your marketing material. If you do not have a logo, we can simply put your studio name in the place of the logo.
                    </p>
                    {logoPreview ? (
                      <div className="flex items-center gap-4">
                        <img src={logoPreview} alt="Logo preview" className="h-16 w-auto rounded border border-warm-200" />
                        <button
                          type="button"
                          onClick={() => {
                            setLogoFile(null);
                            setLogoPreview(null);
                          }}
                          className="text-sm text-danger-600 hover:text-danger-700"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-warm-300 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-warm-50 transition-colors">
                        <svg className="w-8 h-8 text-warm-400 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm text-warm-500">Click to upload logo</span>
                        <span className="text-xs text-warm-400 mt-0.5">JPEG, PNG, or WebP (max 5MB)</span>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setLogoFile(file);
                              const reader = new FileReader();
                              reader.onload = (ev) => setLogoPreview(ev.target?.result as string);
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                      </label>
                    )}
                  </div>

                  {/* Sign-up Method */}
                  <div>
                    <label htmlFor="signup-method" className="block text-sm font-medium text-warm-900 mb-1">
                      How do students sign up for this class?
                    </label>
                    <input
                      id="signup-method"
                      type="text"
                      value={signupMethod}
                      onChange={(e) => setSignupMethod(e.target.value)}
                      placeholder="e.g., www.yourstudio.com/register"
                      required
                      className="input w-full"
                    />
                  </div>

                  {/* Class Cost */}
                  <div>
                    <label htmlFor="class-cost" className="block text-sm font-medium text-warm-900 mb-1">
                      What are you charging students to attend?
                    </label>
                    <input
                      id="class-cost"
                      type="text"
                      value={classCost}
                      onChange={(e) => setClassCost(e.target.value)}
                      placeholder="e.g., $25 per student"
                      required
                      className="input w-full"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmittingMaterials || !signupMethod.trim() || !classCost.trim()}
                    className="btn-primary w-full sm:w-auto disabled:opacity-50"
                  >
                    {isSubmittingMaterials ? 'Submitting...' : 'Submit Marketing Materials'}
                  </button>
                </form>
              )}
            </div>
          )}


          {/* Terms */}
          {booking.terms_accepted_at && (
            <div className="p-4 bg-warm-100 rounded-lg">
              <p className="text-sm text-warm-600">
                Terms accepted on{' '}
                {new Date(booking.terms_accepted_at).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
                {booking.terms_version && ` (Version ${booking.terms_version})`}
              </p>
            </div>
          )}
        </div>

        {/* Back Link */}
        <div className="mt-8">
          <Link
            to="/dashboard/bookings"
            className="text-primary-600 hover:text-primary-700 text-sm font-medium inline-flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to My Bookings
          </Link>
        </div>

        {/* Cancel Confirmation Modal */}
        {showCancelModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-black/50 transition-opacity"
                onClick={() => !isCancelling && setShowCancelModal(false)}
              />

              {/* Modal Content */}
              <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-danger-100 flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-danger-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-warm-900">
                      Cancel Booking
                    </h3>
                    <p className="mt-2 text-warm-600">
                      Are you sure you want to cancel this booking for{' '}
                      <span className="font-medium">{booking.show_name}</span>? This action cannot
                      be undone.
                    </p>
                    {isConfirmed && (
                      <div className="mt-3 p-3 bg-warning-50 border border-warning-200 rounded-lg">
                        {daysUntilClass !== null && daysUntilClass < 15 ? (
                          <>
                            <p className="text-sm font-medium text-warning-800">
                              No refund available
                            </p>
                            <p className="text-sm text-warning-700 mt-1">
                              You are past the refund period. Unfortunately, no payments will be
                              refunded as cancellation is within 15 days of your class.
                            </p>
                          </>
                        ) : daysUntilClass !== null && daysUntilClass <= 30 ? (
                          <>
                            <p className="text-sm font-medium text-warning-800">
                              Partial refund (50%)
                            </p>
                            <p className="text-sm text-warning-700 mt-1">
                              Your deposit of{' '}
                              <span className="font-semibold">
                                {formatPrice(booking.deposit_amount || 0)}
                              </span>{' '}
                              is non-refundable. You are eligible for a 50% refund of the balance
                              paid, as cancellation is within 15–30 days of your class.
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-sm font-medium text-warning-800">
                              Non-refundable deposit
                            </p>
                            <p className="text-sm text-warning-700 mt-1">
                              Your deposit of{' '}
                              <span className="font-semibold">
                                {formatPrice(booking.deposit_amount || 0)}
                              </span>{' '}
                              is non-refundable. The remaining balance paid will be fully refunded.
                            </p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Cancel Error */}
                {cancelError && (
                  <div className="mt-4 p-3 bg-danger-50 border border-danger-200 rounded-lg">
                    <p className="text-sm text-danger-700">{cancelError}</p>
                  </div>
                )}

                {/* Modal Actions */}
                <div className="mt-6 flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => setShowCancelModal(false)}
                    disabled={isCancelling}
                    className="btn bg-warm-100 text-warm-700 hover:bg-warm-200 disabled:opacity-50"
                  >
                    Keep Booking
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelBooking}
                    disabled={isCancelling}
                    className="btn bg-danger-600 text-white hover:bg-danger-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {isCancelling && (
                      <svg
                        className="animate-spin h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    )}
                    {isCancelling ? 'Cancelling...' : 'Yes, Cancel Booking'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment Confirmation Modal */}
        {showPayModal && booking && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div
                className="fixed inset-0 bg-black/50 transition-opacity"
                onClick={() => !isPayingBalance && setShowPayModal(false)}
              />
              <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-warm-900">
                      Confirm Payment
                    </h3>
                    <p className="mt-2 text-warm-600">
                      Your saved card will be charged{' '}
                      <span className="font-semibold text-warm-900">{formatPrice(booking.balance_amount || 0)}</span>{' '}
                      for the remaining balance on this booking.
                    </p>
                    <button
                      type="button"
                      onClick={handleUpdateCard}
                      disabled={isPayingBalance || isUpdatingCard}
                      className="mt-2 text-sm font-medium text-primary-600 hover:text-primary-700 underline disabled:opacity-50"
                    >
                      {isUpdatingCard ? 'Redirecting...' : 'Use a different card'}
                    </button>
                  </div>
                </div>

                <div className="mt-6 flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => setShowPayModal(false)}
                    disabled={isPayingBalance}
                    className="btn bg-warm-100 text-warm-700 hover:bg-warm-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      await handlePayBalance();
                      setShowPayModal(false);
                    }}
                    disabled={isPayingBalance}
                    className="btn-primary disabled:opacity-50 flex items-center gap-2"
                  >
                    {isPayingBalance && (
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    )}
                    {isPayingBalance ? 'Processing...' : `Pay ${formatPrice(booking.balance_amount || 0)}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Ask a Question Modal */}
        {showQuestionModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-warm-900 mb-4">Ask a Question</h3>
              <p className="text-sm text-warm-600 mb-4">
                Have a question about this booking? We'll respond within 24 hours.
              </p>
              <textarea
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                placeholder="Type your question here..."
                maxLength={2000}
                rows={4}
                className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm resize-none"
              />
              <p className="text-xs text-warm-400 mt-1 text-right">{questionText.length}/2000</p>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => { setShowQuestionModal(false); setQuestionText(''); }}
                  className="flex-1 px-4 py-2 border border-warm-300 rounded-lg text-warm-700 hover:bg-warm-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitQuestion}
                  disabled={!questionText.trim() || isSubmittingQuestion}
                  className="flex-1 btn-primary text-sm disabled:opacity-50"
                >
                  {isSubmittingQuestion ? 'Submitting...' : 'Submit Question'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

/**
 * Header Component
 */
interface HeaderProps {
  user: { email?: string } | null;
  logout: () => void;
}

function Header({ user, logout }: HeaderProps) {
  return (
    <header className="bg-white border-b border-warm-200 sticky top-0 z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src="/logo.png" alt="Stage Door Access" className="h-7" />
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            <Link to="/dashboard" className="text-warm-600 hover:text-warm-900 text-sm">
              Dashboard
            </Link>
            <Link
              to="/dashboard/bookings"
              className="text-primary-600 font-medium text-sm"
            >
              My Bookings
            </Link>
            <Link
              to="/dashboard/inquiries"
              className="text-warm-600 hover:text-warm-900 text-sm"
            >
              My Inquiries
            </Link>
            <Link
              to="/dashboard/profile"
              className="text-warm-600 hover:text-warm-900 text-sm"
            >
              Studio Profile
            </Link>
            <Link
              to="/dashboard/contacts"
              className="text-warm-600 hover:text-warm-900 text-sm"
            >
              Contacts
            </Link>
          </nav>

          <div className="flex items-center gap-4">
            <span className="text-warm-600 text-sm hidden sm:block">{user?.email}</span>
            <button
              onClick={() => logout()}
              className="text-warm-600 hover:text-warm-900 text-sm font-medium"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
